import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";
import {
  formatNumberWithCommas,
  formatPercentage,
  formatPhoneNumber,
  parseFormattedNumber,
} from "../../utils/formatters";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import {
  TextInput,
  Button,
  Group,
  Stack,
  Switch,
  Select,
  Textarea,
  Grid,
  Paper,
  Title,
  Table,
  ActionIcon,
  Modal,
  Text,
  Collapse,
  Box,
  Container,
  Chip,
} from "@mantine/core";
import {
  IconX,
  IconPlus,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";

function AddRepPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [repTypes, setRepTypes] = useState([]);

  // Default scale templates
  const [commissionTemplates, setCommissionTemplates] = useState([]);
  const [overrideTemplates, setOverrideTemplates] = useState([]);
  const [marketingTemplates, setMarketingTemplates] = useState([]);

  // Recruiting sources
  const [recruitingSources, setRecruitingSources] = useState([]);

  // Seasons
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [seasonData, setSeasonData] = useState({}); // Maps season ID to { projectedRevenue, revenueGoal, saleStartDate, saleEndDate }
  const [seasonDateErrors, setSeasonDateErrors] = useState({}); // Maps season ID to error messages for date validation

  const [compensationType, setCompensationType] = useState("none"); // none, override, marketing

  const [repData, setRepData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    birthday: "",
    instagram: "",
    linkedin: "",
    recruitedBy: "",
    manager: "",
    repType: "",
    isManager: false,
    repRank: "",
    recruitingSource: "",
    recruitingSourceNotes: "",
    commissionScale: Array.from({ length: 10 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    selectedCommissionTemplate: "",
    hasOverride: false,
    overrideScale: Array.from({ length: 10 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    selectedOverrideTemplate: "",
    hasMarketingDeal: false,
    marketingDealScale: Array.from({ length: 5 }, (_, index) => ({
      id: index + 1,
      revenue: "",
      percentage: "",
    })),
    selectedMarketingTemplate: "",
    notes: "",
  });

  // Format users and managers for dropdowns
  const recruiters = users.map(user => ({
    id: user.rep_id,
    name: `${user.first_name} ${user.last_name}`,
  }));

  const managersForDropdown = managers.map(manager => ({
    id: manager.rep_id,
    name: `${manager.first_name} ${manager.last_name}`,
  }));

  // Load reps and default scale templates
  useEffect(() => {
    loadReps();
    loadDefaultScaleTemplates();
    loadRepTypes();
    loadRecruitingSources();
    loadSeasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default rep type to first one when rep types are loaded
  useEffect(() => {
    if (repTypes.length > 0 && !repData.repRank) {
      setRepData(prev => ({
        ...prev,
        repRank: repTypes[0].id.toString(),
      }));
    }
  }, [repTypes, repData.repRank]);

  // Populate form with lead data if coming from recruiting page
  useEffect(() => {
    const leadData = location.state?.leadData;
    if (leadData) {
      setRepData(prev => ({
        ...prev,
        firstName: leadData.firstName || "",
        lastName: leadData.lastName || "",
        email: leadData.email || "",
        phoneNumber: formatPhoneNumber(leadData.phoneNumber || ""),
        recruitedBy: leadData.recruitedBy || "",
        recruitingSourceNotes: leadData.recruitingSourceNotes || "",
        instagram: leadData.instagram || "",
        linkedin: leadData.linkedinProfile || "",
        recruitingSource: leadData.recruitingSourceId || "",
        manager: leadData.manager || "",
      }));
    }
  }, [location.state]);

  const loadReps = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/rep/reps-list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        setUsers(data.users || []);
        setManagers(data.managers || []);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadDefaultScaleTemplates = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Load commission templates
      const commissionResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/COMMISSION"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (commissionResponse.ok) {
        const commissionData = await commissionResponse.json();
        setCommissionTemplates(commissionData.data || []);
      }

      // Load override templates
      const overrideResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/OVERRIDE"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (overrideResponse.ok) {
        const overrideData = await overrideResponse.json();
        setOverrideTemplates(overrideData.data || []);
      }

      // Load marketing templates
      const marketingResponse = await fetch(
        getApiEndpoint("/v1/scales/default-scales/MD"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (marketingResponse.ok) {
        const marketingData = await marketingResponse.json();
        setMarketingTemplates(marketingData.data || []);
      }
    } catch (err) {
      // Silently handle error - templates are optional
    }
  };

  const loadRepTypes = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/company/rep-types"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRepTypes(result.data || []);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadRecruitingSources = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint("/v1/company/recruiting-sources"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRecruitingSources(result.data || []);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const loadSeasons = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/seasons"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const seasonsData = result.data || [];
        setSeasons(seasonsData);
        // Select all seasons by default
        setSelectedSeasons(seasonsData.map(season => season.id));
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;

    if (name === "phoneNumber") {
      const formattedValue = formatPhoneNumber(value);
      setRepData(prev => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setRepData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleCommissionTemplateChange = value => {
    setRepData(prev => ({
      ...prev,
      selectedCommissionTemplate: value,
    }));

    if (value) {
      const template = commissionTemplates.find(t => t.id === parseInt(value));
      if (template && template.scale_lines) {
        const newCommissionScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setRepData(prev => ({
          ...prev,
          commissionScale: newCommissionScale,
        }));
      }
    }
  };

  const handleOverrideTemplateChange = value => {
    setRepData(prev => ({
      ...prev,
      selectedOverrideTemplate: value,
    }));

    if (value) {
      const template = overrideTemplates.find(t => t.id === parseInt(value));
      if (template && template.scale_lines) {
        const newOverrideScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setRepData(prev => ({
          ...prev,
          overrideScale: newOverrideScale,
        }));
      }
    }
  };

  const handleMarketingTemplateChange = value => {
    setRepData(prev => ({
      ...prev,
      selectedMarketingTemplate: value,
    }));

    if (value) {
      const template = marketingTemplates.find(t => t.id === parseInt(value));
      if (template && template.scale_lines) {
        const newMarketingScale = template.scale_lines.map((line, index) => ({
          id: index + 1,
          revenue: formatNumberWithCommas(line.revenue_amount.toString()),
          percentage: formatPercentage(line.percentage.toString()),
        }));
        setRepData(prev => ({
          ...prev,
          marketingDealScale: newMarketingScale,
        }));
      }
    }
  };

  const handleCommissionChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : field === "percentage"
          ? formatPercentage(value)
          : value;
    setRepData(prev => ({
      ...prev,
      commissionScale: prev.commissionScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const handleOverrideChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : field === "percentage"
          ? formatPercentage(value)
          : value;
    setRepData(prev => ({
      ...prev,
      overrideScale: prev.overrideScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const handleMarketingChange = (id, field, value) => {
    const formattedValue =
      field === "revenue"
        ? formatNumberWithCommas(value)
        : field === "percentage"
          ? formatPercentage(value)
          : value;
    setRepData(prev => ({
      ...prev,
      marketingDealScale: prev.marketingDealScale.map(item =>
        item.id === id ? { ...item, [field]: formattedValue } : item
      ),
    }));
  };

  const addCommissionRow = () => {
    if (repData.commissionScale.length >= 16) return;
    const newId = Math.max(...repData.commissionScale.map(item => item.id)) + 1;
    setRepData(prev => ({
      ...prev,
      commissionScale: [
        ...prev.commissionScale,
        {
          id: newId,
          revenue: "",
          percentage: "",
        },
      ],
    }));
  };

  const removeCommissionRow = id => {
    if (repData.commissionScale.length > 1) {
      setRepData(prev => ({
        ...prev,
        commissionScale: prev.commissionScale.filter(item => item.id !== id),
      }));
    }
  };

  const addOverrideRow = () => {
    if (repData.overrideScale.length >= 16) return;
    const newId = Math.max(...repData.overrideScale.map(item => item.id)) + 1;
    setRepData(prev => ({
      ...prev,
      overrideScale: [
        ...prev.overrideScale,
        {
          id: newId,
          revenue: "",
          percentage: "",
        },
      ],
    }));
  };

  const removeOverrideRow = id => {
    if (repData.overrideScale.length > 1) {
      setRepData(prev => ({
        ...prev,
        overrideScale: prev.overrideScale.filter(item => item.id !== id),
      }));
    }
  };

  const addMarketingRow = () => {
    if (repData.marketingDealScale.length >= 16) return;
    const newId =
      Math.max(...repData.marketingDealScale.map(item => item.id)) + 1;
    setRepData(prev => ({
      ...prev,
      marketingDealScale: [
        ...prev.marketingDealScale,
        {
          id: newId,
          revenue: "",
          percentage: "",
        },
      ],
    }));
  };

  const removeMarketingRow = id => {
    if (repData.marketingDealScale.length > 1) {
      setRepData(prev => ({
        ...prev,
        marketingDealScale: prev.marketingDealScale.filter(
          item => item.id !== id
        ),
      }));
    }
  };

  // Email validation function
  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      recruitedBy,
      manager,
      repRank,
    } = repData;

    const isCompanyAdmin = user?.role === "COMPANY_ADMIN";

    // Check required fields - recruited by and manager are not required for COMPANY_ADMIN
    const requiredFieldsEmpty =
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !repRank ||
      (!isCompanyAdmin && (!recruitedBy || !manager));

    if (requiredFieldsEmpty) {
      const requiredFieldsList = isCompanyAdmin
        ? "First Name, Last Name, Email, Rep Rank"
        : "First Name, Last Name, Email, Recruited By, Manager, Rep Rank";

      return {
        isValid: false,
        message: `Please fill in all required fields (${requiredFieldsList})`,
      };
    }

    // Validate email format
    if (!isValidEmail(email)) {
      return { isValid: false, message: "Please enter a valid email address" };
    }

    // Validate phone number format if provided (optional)
    if (phoneNumber && phoneNumber.trim()) {
      const cleanPhone = phoneNumber.replace(/\D/g, "");
      if (cleanPhone.length !== 10 && cleanPhone.length !== 11) {
        return {
          isValid: false,
          message: "Please enter a valid phone number (10-11 digits)",
        };
      }
    }

    return { isValid: true };
  };

  const handleSeasonToggle = seasonId => {
    setSelectedSeasons(prev => {
      if (prev.includes(seasonId)) {
        // Remove from selected seasons
        const newSelected = prev.filter(id => id !== seasonId);
        // Also remove from seasonData
        setSeasonData(prevData => {
          const newData = { ...prevData };
          delete newData[seasonId];
          return newData;
        });
        return newSelected;
      } else {
        // Add to selected seasons and initialize season data
        setSeasonData(prevData => ({
          ...prevData,
          [seasonId]: {
            projectedRevenue: "",
            revenueGoal: "",
            saleStartDate: "",
            saleEndDate: "",
          },
        }));
        return [...prev, seasonId];
      }
    });
  };

  const handleSeasonDataChange = (seasonId, field, value) => {
    setSeasonData(prev => ({
      ...prev,
      [seasonId]: {
        ...prev[seasonId],
        [field]:
          field === "projectedRevenue" || field === "revenueGoal"
            ? formatNumberWithCommas(value)
            : value,
      },
    }));

    // Validate date fields against season bounds
    if (field === "saleStartDate" || field === "saleEndDate") {
      const season = seasons.find(s => s.id === seasonId);
      if (season && value) {
        const selectedDate = new Date(value);
        const seasonStart = season.start_date
          ? new Date(season.start_date)
          : null;
        const seasonEnd = season.end_date ? new Date(season.end_date) : null;

        let errorMessage = null;

        if (field === "saleStartDate") {
          if (seasonStart && selectedDate < seasonStart) {
            errorMessage = `Start date must be on or after ${seasonStart.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
          } else if (seasonEnd && selectedDate > seasonEnd) {
            errorMessage = `Start date must be on or before ${seasonEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
          }
        } else if (field === "saleEndDate") {
          if (seasonStart && selectedDate < seasonStart) {
            errorMessage = `End date must be on or after ${seasonStart.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
          } else if (seasonEnd && selectedDate > seasonEnd) {
            errorMessage = `End date must be on or before ${seasonEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
          }
        }

        setSeasonDateErrors(prev => ({
          ...prev,
          [seasonId]: {
            ...prev[seasonId],
            [field]: errorMessage,
          },
        }));
      } else {
        // Clear error if value is empty
        setSeasonDateErrors(prev => ({
          ...prev,
          [seasonId]: {
            ...prev[seasonId],
            [field]: null,
          },
        }));
      }
    }
  };

  const handleCompensationTypeChange = type => {
    setCompensationType(type);
    setRepData(prev => ({
      ...prev,
      hasOverride: type === "override",
      hasMarketingDeal: type === "marketing",
    }));
  };

  const handleSave = async () => {
    // Validate required fields first
    const validation = validateForm();
    if (!validation.isValid) {
      setError(validation.message);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare data for API
      const apiData = {
        first_name: repData.firstName,
        last_name: repData.lastName,
        email: repData.email,
        phone_number: repData.phoneNumber
          ? repData.phoneNumber.replace(/\D/g, "")
          : null,
        birthday: repData.birthday || null,
        instagram_username: repData.instagram || null,
        linkedin_profile: repData.linkedin || null,
        recruited_by: repData.recruitedBy
          ? parseInt(repData.recruitedBy)
          : null,
        manager: repData.manager ? parseInt(repData.manager) : null,
        is_manager: repData.isManager,
        rep_type_id: repData.repRank ? parseInt(repData.repRank) : null,
        recruiting_source_id: repData.recruitingSource
          ? parseInt(repData.recruitingSource)
          : null,
        commission_scale: repData.commissionScale
          .filter(item => item.revenue || item.percentage)
          .map(item => ({
            revenue: parseFloat(parseFormattedNumber(item.revenue)) || 0,
            percentage: parseFloat(parseFormattedNumber(item.percentage)) || 0,
          })),
        has_override: repData.hasOverride,
        override_scale: repData.hasOverride
          ? repData.overrideScale
              .filter(item => item.revenue || item.percentage)
              .map(item => ({
                revenue: parseFloat(parseFormattedNumber(item.revenue)) || 0,
                percentage:
                  parseFloat(parseFormattedNumber(item.percentage)) || 0,
              }))
          : [],
        has_marketing_deal: repData.hasMarketingDeal,
        marketing_deal_scale: repData.hasMarketingDeal
          ? repData.marketingDealScale
              .filter(item => item.revenue || item.percentage)
              .map(item => ({
                revenue: parseFloat(parseFormattedNumber(item.revenue)) || 0,
                percentage:
                  parseFloat(parseFormattedNumber(item.percentage)) || 0,
              }))
          : [],
        notes: repData.notes,
        seasons: selectedSeasons.map(seasonId => ({
          season_id: seasonId,
          projected_revenue: seasonData[seasonId]?.projectedRevenue
            ? parseFloat(
                parseFormattedNumber(seasonData[seasonId].projectedRevenue)
              )
            : null,
          rep_revenue_goal: seasonData[seasonId]?.revenueGoal
            ? parseFloat(parseFormattedNumber(seasonData[seasonId].revenueGoal))
            : null,
          sale_start_date: seasonData[seasonId]?.saleStartDate || null,
          sale_end_date: seasonData[seasonId]?.saleEndDate || null,
        })),
      };

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(getApiEndpoint("/v1/rep/add-rep"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(apiData),
      });

      if (response.ok) {
        showSuccess("Rep added successfully!");
        navigate("/reps/downline");
      } else {
        // Try to parse the error response for specific error messages
        let errorMessage = "Failed to create rep";
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }

          // Check for duplicate email error patterns
          if (
            errorMessage.toLowerCase().includes("email") &&
            (errorMessage.toLowerCase().includes("exists") ||
              errorMessage.toLowerCase().includes("duplicate") ||
              errorMessage.toLowerCase().includes("already"))
          ) {
            errorMessage = "Rep with email already exists";
          }
        } catch (parseError) {
          // If we can't parse the response, use the default error message
        }

        throw new Error(errorMessage);
      }
    } catch (err) {
      setError(err.message || "Failed to save rep");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirmation(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirmation(false);
    navigate("/reps/downline");
  };

  const handleCancelConfirmation = () => {
    setShowCancelConfirmation(false);
  };
  return (
    <PageContent
      breadcrumbs={[
        {
          label: "Downline Management",
          path: "/reps/downline",
        },
        {
          label: "Add New Rep",
          path: "",
        },
      ]}
      title="Add New Rep"
    >
      <Box
        style={{
          minHeight: "calc(100vh - 200px)",
          padding: "24px 0",
        }}
      >
        <Container size="xl" style={{ maxWidth: "100%", padding: "0 24px" }}>
          <Grid>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              {/* Personal Information Section */}
              <Paper shadow="sm" p="md" radius="md">
                <Stack gap="md">
                  <Title order={3}>Personal Information</Title>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="First Name"
                        placeholder="Enter first name"
                        value={repData.firstName}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "firstName",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        required
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        label="Last Name"
                        placeholder="Enter last name"
                        value={repData.lastName}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "lastName",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        required
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TextInput
                        type="email"
                        label="Email"
                        placeholder="Enter email address"
                        value={repData.email}
                        onChange={event =>
                          handleInputChange({
                            target: {
                              name: "email",
                              value: event.currentTarget.value,
                            },
                          })
                        }
                        required
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Box>
                        <Box style={{ height: "21px", marginBottom: "4px" }} />
                        <Paper
                          p="sm"
                          radius="sm"
                          shadow="none"
                          style={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #ced4da",
                            height: "42px",
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "16px",
                            paddingRight: "16px",
                          }}
                        >
                          <Group
                            justify="space-between"
                            align="center"
                            w="100%"
                          >
                            <Text size="sm" fw={500} c="#1f2937">
                              Has Manager Privileges?
                            </Text>
                            <Switch
                              checked={repData.isManager}
                              onChange={event =>
                                handleInputChange({
                                  target: {
                                    name: "isManager",
                                    checked: event.currentTarget.checked,
                                    type: "checkbox",
                                  },
                                })
                              }
                              disabled={loading}
                              size="lg"
                              onLabel="YES"
                              offLabel="NO"
                              styles={{
                                track: {
                                  width: "70px",
                                },
                              }}
                            />
                          </Group>
                        </Paper>
                      </Box>
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Recruited By"
                        placeholder="Select recruiter..."
                        data={recruiters.map(recruiter => ({
                          value: recruiter.id.toString(),
                          label: recruiter.name,
                        }))}
                        value={repData.recruitedBy}
                        onChange={value =>
                          handleInputChange({
                            target: {
                              name: "recruitedBy",
                              value: value,
                            },
                          })
                        }
                        required={user?.role !== "COMPANY_ADMIN"}
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Manager"
                        placeholder="Select manager..."
                        data={managersForDropdown.map(manager => ({
                          value: manager.id.toString(),
                          label: manager.name,
                        }))}
                        value={repData.manager}
                        onChange={value =>
                          handleInputChange({
                            target: {
                              name: "manager",
                              value: value,
                            },
                          })
                        }
                        required={user?.role !== "COMPANY_ADMIN"}
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Rep Rank"
                        placeholder="Select rep rank..."
                        data={repTypes.map(repType => ({
                          value: repType.id.toString(),
                          label: repType.name,
                        }))}
                        value={repData.repRank}
                        onChange={value =>
                          handleInputChange({
                            target: {
                              name: "repRank",
                              value: value,
                            },
                          })
                        }
                        required
                        disabled={loading}
                        size="md"
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Recruiting Source"
                        placeholder="Select recruiting source..."
                        data={recruitingSources.map(source => ({
                          value: source.id.toString(),
                          label: source.source_name,
                        }))}
                        value={repData.recruitingSource}
                        onChange={value =>
                          handleInputChange({
                            target: {
                              name: "recruitingSource",
                              value: value,
                            },
                          })
                        }
                        disabled={loading}
                        clearable
                        size="md"
                      />
                    </Grid.Col>
                  </Grid>

                  {/* Seasons Section */}
                  {seasons.length > 0 && (
                    <Box>
                      <Title order={4} mb="sm">
                        Seasons
                      </Title>
                      <Chip.Group multiple value={selectedSeasons.map(String)}>
                        <Group gap="xs">
                          {seasons.map(season => (
                            <Chip
                              key={season.id}
                              value={String(season.id)}
                              checked={selectedSeasons.includes(season.id)}
                              onChange={() => handleSeasonToggle(season.id)}
                              disabled={loading}
                            >
                              {season.name}
                            </Chip>
                          ))}
                        </Group>
                      </Chip.Group>

                      {/* Season-specific data sections */}
                      {selectedSeasons.length > 0 && (
                        <Stack gap="md" mt="md">
                          {selectedSeasons.map(seasonId => {
                            const season = seasons.find(s => s.id === seasonId);
                            const formatDate = dateString => {
                              if (!dateString) return null;
                              const date = new Date(dateString);
                              return date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              });
                            };
                            const seasonDates =
                              season?.start_date || season?.end_date
                                ? ` (${formatDate(season?.start_date) || "N/A"} - ${formatDate(season?.end_date) || "N/A"})`
                                : "";
                            return (
                              <Paper key={seasonId} p="md" withBorder>
                                <Stack gap="md">
                                  <Title order={5}>
                                    {season?.name} Details
                                    {seasonDates && (
                                      <Text
                                        component="span"
                                        size="sm"
                                        fs="italic"
                                        fw={400}
                                        c="dimmed"
                                        ml="xs"
                                      >
                                        {seasonDates}
                                      </Text>
                                    )}
                                  </Title>
                                  <Grid>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                      <TextInput
                                        label="Projected Revenue"
                                        placeholder="Enter projected revenue"
                                        value={
                                          seasonData[seasonId]
                                            ?.projectedRevenue || ""
                                        }
                                        onChange={event =>
                                          handleSeasonDataChange(
                                            seasonId,
                                            "projectedRevenue",
                                            event.currentTarget.value
                                          )
                                        }
                                        disabled={loading}
                                        size="md"
                                      />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                      <TextInput
                                        label="Rep Revenue Goal"
                                        placeholder="Enter rep revenue goal"
                                        value={
                                          seasonData[seasonId]?.revenueGoal ||
                                          ""
                                        }
                                        onChange={event =>
                                          handleSeasonDataChange(
                                            seasonId,
                                            "revenueGoal",
                                            event.currentTarget.value
                                          )
                                        }
                                        disabled={loading}
                                        size="md"
                                      />
                                    </Grid.Col>
                                  </Grid>
                                  <Grid>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                      <TextInput
                                        type="date"
                                        label="Sale Start Date"
                                        value={
                                          seasonData[seasonId]?.saleStartDate ||
                                          ""
                                        }
                                        onChange={event =>
                                          handleSeasonDataChange(
                                            seasonId,
                                            "saleStartDate",
                                            event.currentTarget.value
                                          )
                                        }
                                        disabled={loading}
                                        size="md"
                                        error={
                                          seasonDateErrors[seasonId]
                                            ?.saleStartDate
                                        }
                                        min={season?.start_date || undefined}
                                        max={season?.end_date || undefined}
                                      />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                      <TextInput
                                        type="date"
                                        label="Sale End Date"
                                        value={
                                          seasonData[seasonId]?.saleEndDate ||
                                          ""
                                        }
                                        onChange={event =>
                                          handleSeasonDataChange(
                                            seasonId,
                                            "saleEndDate",
                                            event.currentTarget.value
                                          )
                                        }
                                        disabled={loading}
                                        size="md"
                                        error={
                                          seasonDateErrors[seasonId]
                                            ?.saleEndDate
                                        }
                                        min={season?.start_date || undefined}
                                        max={season?.end_date || undefined}
                                      />
                                    </Grid.Col>
                                  </Grid>
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>
                  )}

                  {/* Optional Section - Collapsible */}
                  <Box>
                    <Group
                      gap="md"
                      style={{ cursor: "pointer" }}
                      justify="space-between"
                      onClick={() =>
                        setShowAdditionalDetails(!showAdditionalDetails)
                      }
                    >
                      <Title order={4}>Optional Notes & Details</Title>
                      <ActionIcon variant="transparent" size="sm">
                        {showAdditionalDetails ? (
                          <IconChevronUp size={20} />
                        ) : (
                          <IconChevronDown size={20} />
                        )}
                      </ActionIcon>
                    </Group>

                    <Collapse in={showAdditionalDetails}>
                      <Stack gap="md" mt="md">
                        <Grid>
                          <Grid.Col span={{ base: 12, sm: 6 }}>
                            <TextInput
                              label="Phone Number"
                              placeholder="(000) 000-0000"
                              value={repData.phoneNumber}
                              onChange={event =>
                                handleInputChange({
                                  target: {
                                    name: "phoneNumber",
                                    value: event.currentTarget.value,
                                  },
                                })
                              }
                              disabled={loading}
                              size="md"
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 6 }}>
                            <TextInput
                              type="date"
                              label="Birthday"
                              value={repData.birthday}
                              onChange={event =>
                                handleInputChange({
                                  target: {
                                    name: "birthday",
                                    value: event.currentTarget.value,
                                  },
                                })
                              }
                              disabled={loading}
                              size="md"
                            />
                          </Grid.Col>
                        </Grid>

                        <Grid>
                          <Grid.Col span={{ base: 12, sm: 6 }}>
                            <TextInput
                              label="Instagram"
                              placeholder="@username or profile URL"
                              value={repData.instagram}
                              onChange={event =>
                                handleInputChange({
                                  target: {
                                    name: "instagram",
                                    value: event.currentTarget.value,
                                  },
                                })
                              }
                              disabled={loading}
                              size="md"
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 6 }}>
                            <TextInput
                              label="LinkedIn"
                              placeholder="LinkedIn profile URL"
                              value={repData.linkedin}
                              onChange={event =>
                                handleInputChange({
                                  target: {
                                    name: "linkedin",
                                    value: event.currentTarget.value,
                                  },
                                })
                              }
                              disabled={loading}
                              size="md"
                            />
                          </Grid.Col>
                        </Grid>
                      </Stack>
                    </Collapse>
                  </Box>
                </Stack>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 6 }}>
              {/* Compensation Section */}
              <Paper shadow="sm" p="md" radius="md">
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Title order={3}>Compensation</Title>
                    <Group gap="xs">
                      <Button
                        variant={
                          compensationType === "none" ? "filled" : "outline"
                        }
                        size="sm"
                        onClick={() => handleCompensationTypeChange("none")}
                        disabled={loading}
                      >
                        Personal Commission
                      </Button>
                      <Button
                        variant={
                          compensationType === "override" ? "filled" : "outline"
                        }
                        size="sm"
                        onClick={() => handleCompensationTypeChange("override")}
                        disabled={loading}
                      >
                        Override
                      </Button>
                      <Button
                        variant={
                          compensationType === "marketing"
                            ? "filled"
                            : "outline"
                        }
                        size="sm"
                        onClick={() =>
                          handleCompensationTypeChange("marketing")
                        }
                        disabled={loading}
                      >
                        Marketing Deal
                      </Button>
                    </Group>
                  </Group>

                  <Stack gap="md">
                    {compensationType === "none" &&
                      commissionTemplates.length > 0 && (
                        <Select
                          label="Use Default Scale Template"
                          placeholder="Select a template..."
                          data={commissionTemplates.map(template => ({
                            value: template.id.toString(),
                            label: template.name,
                          }))}
                          value={repData.selectedCommissionTemplate}
                          onChange={handleCommissionTemplateChange}
                          disabled={loading}
                          clearable
                          size="md"
                        />
                      )}

                    {compensationType === "none" && (
                      <>
                        <Table striped>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Percentage (%)</Table.Th>
                              <Table.Th>Revenue ($)</Table.Th>
                              <Table.Th width={50}></Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody style={{ border: "1px solid #dee2e6" }}>
                            {repData.commissionScale.map(item => (
                              <Table.Tr key={item.id}>
                                <Table.Td>
                                  <TextInput
                                    value={item.percentage}
                                    onChange={event =>
                                      handleCommissionChange(
                                        item.id,
                                        "percentage",
                                        event.currentTarget.value
                                      )
                                    }
                                    placeholder="0.0"
                                    disabled={loading}
                                    size="sm"
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <TextInput
                                    value={item.revenue}
                                    onChange={event =>
                                      handleCommissionChange(
                                        item.id,
                                        "revenue",
                                        event.currentTarget.value
                                      )
                                    }
                                    placeholder="0.00"
                                    disabled={loading}
                                    size="sm"
                                  />
                                </Table.Td>
                                <Table.Td>
                                  {repData.commissionScale.length > 1 && (
                                    <ActionIcon
                                      color="red"
                                      variant="light"
                                      onClick={() =>
                                        removeCommissionRow(item.id)
                                      }
                                      disabled={loading}
                                      size="sm"
                                    >
                                      <IconX size={16} />
                                    </ActionIcon>
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>

                        <Button
                          leftSection={<IconPlus size={16} />}
                          variant="outline"
                          onClick={addCommissionRow}
                          disabled={
                            repData.commissionScale.length >= 16 || loading
                          }
                          size="sm"
                        >
                          Add Row{" "}
                          {repData.commissionScale.length >= 16
                            ? "(Max 16)"
                            : ""}
                        </Button>
                      </>
                    )}

                    {compensationType === "override" && (
                      <>
                        {overrideTemplates.length > 0 && (
                          <Select
                            label="Use Default Override Template"
                            placeholder="Select a template..."
                            data={overrideTemplates.map(template => ({
                              value: template.id.toString(),
                              label: template.name,
                            }))}
                            value={repData.selectedOverrideTemplate}
                            onChange={handleOverrideTemplateChange}
                            disabled={loading}
                            clearable
                            size="md"
                          />
                        )}

                        <Table striped>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Percentage (%)</Table.Th>
                              <Table.Th>Revenue ($)</Table.Th>
                              <Table.Th width={50}></Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody style={{ border: "1px solid #dee2e6" }}>
                            {compensationType === "override" &&
                              repData.overrideScale.map(item => (
                                <Table.Tr key={item.id}>
                                  <Table.Td>
                                    <TextInput
                                      value={item.percentage}
                                      onChange={event =>
                                        handleOverrideChange(
                                          item.id,
                                          "percentage",
                                          event.currentTarget.value
                                        )
                                      }
                                      placeholder="0.0"
                                      disabled={
                                        compensationType === "none" || loading
                                      }
                                      size="sm"
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <TextInput
                                      value={item.revenue}
                                      onChange={event =>
                                        handleOverrideChange(
                                          item.id,
                                          "revenue",
                                          event.currentTarget.value
                                        )
                                      }
                                      placeholder="0.00"
                                      disabled={
                                        compensationType === "none" || loading
                                      }
                                      size="sm"
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    {repData.overrideScale.length > 1 && (
                                      <ActionIcon
                                        color="red"
                                        variant="light"
                                        onClick={() =>
                                          removeOverrideRow(item.id)
                                        }
                                        disabled={
                                          compensationType === "none" || loading
                                        }
                                        size="sm"
                                      >
                                        <IconX size={16} />
                                      </ActionIcon>
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                              ))}

                            {compensationType === "marketing" &&
                              repData.marketingDealScale.map(item => (
                                <Table.Tr key={item.id}>
                                  <Table.Td>
                                    <TextInput
                                      value={item.percentage}
                                      onChange={event =>
                                        handleMarketingChange(
                                          item.id,
                                          "percentage",
                                          event.currentTarget.value
                                        )
                                      }
                                      placeholder="0.0"
                                      disabled={
                                        compensationType === "none" || loading
                                      }
                                      size="sm"
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <TextInput
                                      value={item.revenue}
                                      onChange={event =>
                                        handleMarketingChange(
                                          item.id,
                                          "revenue",
                                          event.currentTarget.value
                                        )
                                      }
                                      placeholder="0.00"
                                      disabled={
                                        compensationType === "none" || loading
                                      }
                                      size="sm"
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    {repData.marketingDealScale.length > 1 && (
                                      <ActionIcon
                                        color="red"
                                        variant="light"
                                        onClick={() =>
                                          removeMarketingRow(item.id)
                                        }
                                        disabled={
                                          compensationType === "none" || loading
                                        }
                                        size="sm"
                                      >
                                        <IconX size={16} />
                                      </ActionIcon>
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                          </Table.Tbody>
                        </Table>

                        <Button
                          leftSection={<IconPlus size={16} />}
                          variant="outline"
                          onClick={addOverrideRow}
                          disabled={
                            repData.overrideScale.length >= 16 || loading
                          }
                          size="sm"
                        >
                          Add Row{" "}
                          {repData.overrideScale.length >= 16 ? "(Max 16)" : ""}
                        </Button>
                      </>
                    )}

                    {compensationType === "marketing" && (
                      <>
                        {marketingTemplates.length > 0 && (
                          <Select
                            label="Use Default Marketing Template"
                            placeholder="Select a template..."
                            data={marketingTemplates.map(template => ({
                              value: template.id.toString(),
                              label: template.name,
                            }))}
                            value={repData.selectedMarketingTemplate}
                            onChange={handleMarketingTemplateChange}
                            disabled={loading}
                            clearable
                            size="md"
                          />
                        )}

                        <Table striped>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Percentage (%)</Table.Th>
                              <Table.Th>Revenue ($)</Table.Th>
                              <Table.Th width={50}></Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody style={{ border: "1px solid #dee2e6" }}>
                            {repData.marketingDealScale.map(item => (
                              <Table.Tr key={item.id}>
                                <Table.Td>
                                  <TextInput
                                    value={item.percentage}
                                    onChange={event =>
                                      handleMarketingChange(
                                        item.id,
                                        "percentage",
                                        event.currentTarget.value
                                      )
                                    }
                                    placeholder="0.0"
                                    disabled={loading}
                                    size="sm"
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <TextInput
                                    value={item.revenue}
                                    onChange={event =>
                                      handleMarketingChange(
                                        item.id,
                                        "revenue",
                                        event.currentTarget.value
                                      )
                                    }
                                    placeholder="0.00"
                                    disabled={loading}
                                    size="sm"
                                  />
                                </Table.Td>
                                <Table.Td>
                                  {repData.marketingDealScale.length > 1 && (
                                    <ActionIcon
                                      color="red"
                                      variant="light"
                                      onClick={() =>
                                        removeMarketingRow(item.id)
                                      }
                                      disabled={loading}
                                      size="sm"
                                    >
                                      <IconX size={16} />
                                    </ActionIcon>
                                  )}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>

                        <Button
                          leftSection={<IconPlus size={16} />}
                          variant="outline"
                          onClick={addMarketingRow}
                          disabled={
                            repData.marketingDealScale.length >= 16 || loading
                          }
                          size="sm"
                        >
                          Add Row{" "}
                          {repData.marketingDealScale.length >= 16
                            ? "(Max 16)"
                            : ""}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>

          {/* Notes Section */}
          <Paper shadow="sm" p="md" radius="md" mt="lg">
            <Stack gap="md">
              <Title order={3}>Notes</Title>
              <Textarea
                placeholder="Enter any additional notes about this rep..."
                rows={4}
                value={repData.notes}
                onChange={event =>
                  handleInputChange({
                    target: {
                      name: "notes",
                      value: event.currentTarget.value,
                    },
                  })
                }
                disabled={loading}
              />
            </Stack>
          </Paper>

          {/* Footer buttons with Mantine components */}
          <Group justify="flex-end" mt="lg">
            <Button
              variant="outline"
              onClick={handleCancelClick}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading} loading={loading}>
              Save Rep
            </Button>
          </Group>
        </Container>
      </Box>

      {/* Error message */}
      {error && <DisplayMessage type="error" message={error} />}

      {/* Confirmation Modal */}
      <Modal
        opened={showCancelConfirmation}
        onClose={handleCancelConfirmation}
        title="Cancel Adding Rep?"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to cancel? All entered data will be lost.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={handleCancelConfirmation}>
              Continue Editing
            </Button>
            <Button onClick={handleConfirmCancel}>Yes, Cancel</Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
}

export default AddRepPage;
